import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from '../../../util/pageTitles';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';

import NoData from '../../../components/NoData/NoData';
import CardPlaceholder from '../../../components/CardPlaceholder';
import { setChart, saveConfig, updateMetrics, updateLineChart } from '../../../components/Filter/FilterActions';
import { setPageName } from './../../App/AppActions';
import { getData } from '../CTSPActions';

import Filter from '../../../components/Filter/Filter';
import { DatePicker, Options } from '../../../components/Filter/components';

import CTSPChart from '../components/CTSPChart';
import DetailedOverview from '../components/DetailedOverview';
import DetailsAccordion from '../../../components/DetailsAccordion/DetailsAccordion';
import PercentilesTrendChart from '../components/PercentilesTrendChart';
import PercentileItemsPanel from '../components/PercentileItemsPanel';
import { PERCENTILE_ITEMS_PANEL_WIDTH } from '../CTSPConstants';

import { 
  trackTeamDashboardView, 
  trackChartPointDetailsView,
  trackHowToOpened 
} from '../../../util/mixpanel';

import './../CTSP.scss';
import ChartDownloadMenu from '../../../components/ChartDownloadMenu';

class CTSP extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedPoint: {},
      selectedGroup: null,
      panelOverlap: 0
    };

    this.contentRef = React.createRef();

    this.handlePointClick = this.handlePointClick.bind(this);
    this.handleGroupClick = this.handleGroupClick.bind(this);
    this.handlePanelClose = this.handlePanelClose.bind(this);

    props.dispatch(setPageName('Cycle time scatter plot'));
  }

  componentDidMount() {
    if (this.props.dashboard && this.props.dashboard._id && this.props.dataIsLoading === false) {
      this.props.setChart('ctsp');

      trackTeamDashboardView('ctsp');
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.selectedGroup && prevProps.filters !== this.props.filters) {
      this.handlePanelClose();
    }
    if (!prevState.selectedGroup !== !this.state.selectedGroup) {
      window.dispatchEvent(new Event('resize'));
    }
    if (
      (prevProps.dataHash != this.props.dataHash ||
        prevProps.hash != this.props.hash ||
        prevProps.enabledColors != this.props.enabledColors ||
        prevProps.selectedDate != this.props.selectedDate) &&
      this.props.dataIsLoading === false
    ) {
      this.handleChange();
    }
  }

  handleChange(force) {
    if (force || !this.props.dataIsLoading) {
      this.setState({
        selectedPoint: {},
        selectedGroup: null
      });
      this.props.getData();
    }
  }

  handlePointClick(point) {
    this.setState({
      isDetailsOpen: true,
      selectedPoint: point
    });

    trackChartPointDetailsView('ctsp', true);
  }

  handleGroupClick(group) {
    if (this.props.isLoading) {
      return;
    }

    const { right } = this.contentRef.current.getBoundingClientRect();

    this.setState({
      selectedGroup: group,
      panelOverlap: Math.max(0, PERCENTILE_ITEMS_PANEL_WIDTH - (window.innerWidth - right))
    });
  }

  handlePanelClose() {
    this.setState({
      selectedGroup: null
    });
  }

  trackHowToCTSPOpen() {
    trackHowToOpened('ctsp');
  }

  handleCloseMetrics = () => {
    this.props.closeMetrics();
    this.props.saveConfig();
  };

  handleClosePercentiles = () => {
    this.props.closePercentiles();
    this.props.saveConfig();
  };

  render() {
    const tooltip = (
      <div className="more_info_tooltip">
        <div className="more_info-inner">
          <h4>Cycle Time Scatterplot</h4>
          <p>Each dot is a completed work item, positioned by its completion or start date and cycle time. Dashed lines mark the selected percentiles.</p>
          <p><b>Look for:</b> high dots, which can indicate completed items with longer cycle times.</p>
          <a
            href={`https://getnave.com/cycle-time-scatterplot`}
            target="_blank"
            onClick={this.trackHowToCFDOpen}
            onAuxClick={this.trackHowToCFDOpen}
            className='more_info-link'
          >
            Learn more →
          </a>
        </div>
      </div>
    );

    const totalItems = !this.props.data ? 0 : _.reduce(this.props.data.series, (p, c) => {
      return p + (c.data && c.data.length || 0);
    }, 0);

    return (
      <div
        className="content-wrap"
        ref={this.contentRef}
        style={this.state.selectedGroup ? { paddingRight: this.state.panelOverlap } : undefined}
      >
        <Helmet>
          <title>{pageTitle(`Cycle Time Scatterplot${this.props.dashboard ? ' | ' + this.props.dashboard.name : ''}`)}</title>
        </Helmet>
        <Filter
          totalItems={totalItems}
          showMetrics
          enablePercentiles
          enablePercentileTrends={this.props.lineChart}
          enableTrendline
          enableSelectedDate
          enableMarkBlocked
          timeunit
          dateformat
        />
        <div className="chart_box_view">
          <div className="dashboard_head">
            <div className="flex-layout">
              <h1>
                Cycle Time Scatterplot
              </h1>
              <div className="more_info_content">
                <span className="more_info"></span>
                {tooltip}
              </div>
            </div>
            <div className="button_tools">
              <Options showMetrics showLineChart options={{ lineChartTooltip: 'Percentiles chart' }} />
              <DatePicker />
            </div>
          </div>
          <div>
            <div className="dashboard_details">
              {this.props.metrics && (this.props.data.series && !!this.props.data.series.length ? (
                <DetailedOverview id="details" data={this.props.data.series} focusable />
              ) : (
                <CardPlaceholder title="Process metrics" shape="table" loading={this.props.isLoading} onClose={this.handleCloseMetrics} />
              ))}
              {this.props.lineChart && (this.props.percentileTrends && !!this.props.percentileTrends.length ? (
                <PercentilesTrendChart
                  data={this.props.percentileTrends}
                  groups={this.props.percentileGroups}
                  axis={this.props.percentileAxis}
                  widget="ptc"
                  selectedGroup={this.state.selectedGroup}
                  onPointClick={this.handleGroupClick}
                />
              ) : (
                <CardPlaceholder title="Percentiles" shape="table" loading={this.props.isLoading} onClose={this.handleClosePercentiles} />
              ))}
            </div>
            <div className="chart_row">
              <div className="diagram_details">
                <ChartDownloadMenu chartId="CTSP" />
                <CTSPChart id="CTSP" data={this.props.data} onPointClick={this.handlePointClick} isPointSelected={!!this.state.isDetailsOpen} />
                <NoData show={this.props.data.series && !this.props.data.series.length} loading={this.props.isLoading} />
              </div>
              <DetailsAccordion
                id="details-accordion"
                data={this.state.selectedPoint}
                chart="ctsp"
                onClose={() => this.setState({ isDetailsOpen: false })}
                blockedTime={this.props.blockedTime}
                blockers={this.props.blockers}
                dateLabel={`${_.upperFirst(this.props.selectedDate)} date`}
                quietDateLabel
              />
            </div>
          </div>
        </div>
        {this.state.selectedGroup && (
          <PercentileItemsPanel
            group={this.state.selectedGroup}
            blockedTime={this.props.blockedTime}
            blockers={this.props.blockers}
            onHide={this.handlePanelClose}
          />
        )}
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setChart: name => dispatch(setChart(name)),
    getData: () => dispatch(getData()),
    closeMetrics: () => dispatch(updateMetrics(false, 'ctsp')),
    closePercentiles: () => dispatch(updateLineChart(false, 'ctsp')),
    saveConfig: () => dispatch(saveConfig())
  };
};

export const mapStateToProps = state => {
  return {
    enabledColors: state.filters.enabledColors,
    filters: state.filters.ctsp,
    lineChart: state.filters.ctsp.lineChart,
    blockedTime: state.filters.ctsp.blockedTime,
    blockers: state.filters.ctsp.blockers,
    hash: state.filters.ctsp.hash,
    timeUnit: state.filters.ctsp.timeUnit,
    selectedDate: state.filters.ctsp.selectedDate,
    metrics: state.filters.ctsp.metrics,
    data: state.ctsp.data,
    percentileTrends: state.ctsp.percentileTrends,
    percentileGroups: state.ctsp.percentileGroups,
    percentileAxis: state.ctsp.percentileAxis,
    isLoading: state.ctsp.isLoading,
    dashboard: state.dashboards.selected,
    dataIsLoading: state.app.isLoading,
    dataHash: state.app.dataHash,
    app: state.app.app
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CTSP);
